import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import './style.scss';
import { useEffect, useRef, useState } from 'react';
import imageCompression from 'browser-image-compression';
import Button from '../../ui/Button';
import RemoveIcon from '../../assets/icons/close.svg';
import ButtonsGroup from '../../ui/ButtonsGroup';
import { STATIC_IMAGES } from '../../app.config.vars/staticImages';
import { useTranslation } from 'react-i18next';
import Tooltip from '../../ui/Tooltip';
import InfoIcon from '../../assets/icons/info.svg';
import { useAppDispatch } from '../../store';
import { setIsModalOpen, setModalContent, setModalContentProps } from '../../store/slices/global';
import { MODAL_CONTENT } from '../../app.config.vars/modalContent';
const FileUploader = ({ filePreview, selectedFile, onChange, maxImageSizeMB, disabled, error, errorMessage, accept, buttonLabel, buttonVariant, useCamera, buttonIcon, previewStyles, label, additionalLabel, tooltipContent, maxFileSizeMB, imageCompressionSizeMB, }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const fileInputRef = useRef(null);
    const [fileType, setFileType] = useState(null);
    const [isCorrectMediaType, setIsCorrectMediaType] = useState(false);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }
        try {
            if (file.type.includes('image')) {
                if (maxImageSizeMB && file.size > maxImageSizeMB * 1024 * 1024) {
                    onChange(null);
                    event.currentTarget.value = '';
                    dispatch(setIsModalOpen(true));
                    dispatch(setModalContent(MODAL_CONTENT.cannotUploadFile.key));
                    dispatch(setModalContentProps({ maxSize: maxImageSizeMB }));
                }
                else {
                    const compressedFile = await imageCompression(file, {
                        maxSizeMB: imageCompressionSizeMB,
                        useWebWorker: true,
                    });
                    onChange(compressedFile);
                }
            }
            else {
                if (maxFileSizeMB && file.size > maxFileSizeMB * 1024 * 1024) {
                    onChange(null);
                    event.currentTarget.value = '';
                    dispatch(setIsModalOpen(true));
                    dispatch(setModalContent(MODAL_CONTENT.cannotUploadFile.key));
                    dispatch(setModalContentProps({ maxSize: maxFileSizeMB }));
                }
                else {
                    onChange(file);
                }
            }
        }
        catch (error) {
            console.error('Compression error:', error);
        }
    };
    const openCamera = () => {
        if (fileInputRef.current) {
            fileInputRef.current.setAttribute('capture', 'environment');
            fileInputRef.current.click();
        }
    };
    const openGallery = () => {
        if (fileInputRef.current) {
            fileInputRef.current.removeAttribute('capture');
            fileInputRef.current.click();
        }
    };
    const handleDelete = () => {
        fileInputRef.current.value = '';
        onChange(null);
    };
    useEffect(() => {
        const fileType = selectedFile?.includes('data:image')
            ? 'image'
            : selectedFile?.includes('data:video')
                ? 'video'
                : selectedFile?.includes('data:audio')
                    ? 'audio'
                    : 'unknown';
        setFileType(fileType);
        setIsCorrectMediaType(!(fileType === 'unknown'));
    }, [selectedFile]);
    return (_jsxs("div", { className: "file-uploader", children: [label && (_jsxs("div", { className: "label", children: [label, tooltipContent && (_jsx(Tooltip, { content: tooltipContent, position: "top", children: _jsx(InfoIcon, { style: {
                                width: '16px',
                                height: '16px',
                                marginLeft: '4px',
                                cursor: 'help',
                                display: 'inline-block',
                                verticalAlign: 'middle',
                            } }) })), !tooltipContent && (_jsx(Tooltip, { content: t('tooltip.file_upload'), position: "top", children: _jsx(InfoIcon, { style: {
                                width: '16px',
                                height: '16px',
                                marginLeft: '4px',
                                cursor: 'help',
                                display: 'inline-block',
                                verticalAlign: 'middle',
                            } }) }))] })), additionalLabel && _jsx("div", { className: "additional-label", children: additionalLabel }), _jsxs("div", { className: "file-uploader-wrapper", children: [_jsx("div", { className: "file-uploader-preview", style: previewStyles, children: selectedFile && isCorrectMediaType ? (fileType === 'image' ? (_jsxs(_Fragment, { children: [_jsx("img", { src: selectedFile, alt: "Preview", className: 'file-preview image' }), !disabled && (_jsx(Button, { shape: 'square', icon: _jsx(RemoveIcon, {}), iconSize: 'small', onClick: handleDelete, onKeyDown: (event) => {
                                        if (event.code === 'Space') {
                                            event.preventDefault();
                                            handleDelete();
                                        }
                                    } }))] })) : fileType === 'video' ? (_jsxs(_Fragment, { children: [_jsx("video", { src: selectedFile, className: 'file-preview video', controls: true }), !disabled && (_jsx(Button, { shape: 'square', icon: _jsx(RemoveIcon, {}), iconSize: 'small', onClick: handleDelete, onKeyDown: (event) => {
                                        if (event.code === 'Space') {
                                            event.preventDefault();
                                            handleDelete();
                                        }
                                    } }))] })) : (_jsxs(_Fragment, { children: [_jsx("audio", { src: selectedFile, className: 'file-preview audio', controls: true }), !disabled && (_jsx(Button, { shape: 'square', icon: _jsx(RemoveIcon, {}), iconSize: 'small', onClick: handleDelete, onKeyDown: (event) => {
                                        if (event.code === 'Space') {
                                            event.preventDefault();
                                            handleDelete();
                                        }
                                    } }))] }))) : filePreview ? (_jsx("img", { src: filePreview, alt: 'preview image', className: 'file-preview' })) : (_jsx("img", { src: localStorage.getItem(STATIC_IMAGES.imagePreview.key) || undefined, alt: 'image icon', className: 'image-preview-icon' })) }), error && errorMessage && _jsx("div", { className: "form-field-error-message", children: errorMessage }), !disabled && (_jsxs(_Fragment, { children: [_jsx("div", { className: "file-uploader-input", children: _jsx("input", { ref: fileInputRef, type: "file", accept: accept, onChange: handleFileSelect, tabIndex: -1 }) }), _jsx("div", { className: "file-uploader-controls", children: _jsxs(ButtonsGroup, { children: [useCamera && isMobile && (_jsx(Button, { onClick: openCamera, label: t('button.open_camera'), onKeyDown: (event) => {
                                                if (event.code === 'Space') {
                                                    event.preventDefault();
                                                    openCamera();
                                                }
                                            } })), _jsx(Button, { type: "button", onClick: openGallery, label: buttonLabel, variant: buttonVariant, icon: buttonIcon, manageIcon: 'fill', onKeyDown: (event) => {
                                                if (event.code === 'Space') {
                                                    event.preventDefault();
                                                    openGallery();
                                                }
                                            } })] }) })] }))] })] }));
};
export default FileUploader;
